// features/organization/sitesLocations/SiteModal.jsx

import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";

const EMPTY_FORM = {
  site_name: "",
  site_type: "",
  department_id: "",
  legal_entity_id: "",
  address: "",
  
  country: "",
  country_id: "",
  state: "",
  state_id: "",
  city: "",
  pincode: "",
};

const SiteModal = ({ onClose, onSaved, editData }) => {
  const [form, setForm] = useState(editData || EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [siteTypes, setSiteTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [loading, setLoading] = useState(true);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const fetchOptions = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const org_id = user?.org_id ?? 1;

      try {
        const [typesRes, deptsRes, entitiesRes] = await Promise.all([
          apiClient.get("/api/sitetypes"),
          apiClient.get("/api/department-list"),
          apiClient.get(`/api/legal-entities-list?org_id=${org_id}`),
        ]);

        setSiteTypes(typesRes.data.data ?? []);
        setDepartments(deptsRes.data.data ?? []);

        if (entitiesRes.data.code === 200) {
          setLegalEntities(entitiesRes.data.data ?? []);
        }
      } catch (err) {
        console.error("Failed to fetch dropdown options:", err);
      } finally {
        setLoading(false);
        setEntitiesLoading(false);
      }
    };

    fetchOptions();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
    setApiError("");
  };

  const validate = () => {
    const e = {};
    if (!form.site_name.trim()) e.site_name = "Site name is required.";
    if (!form.site_type) e.site_type = "Site type is required.";

    if (!form.legal_entity_id) e.legal_entity_id = "Legal entity is required.";
    if (!form.address.trim()) e.address = "Address is required.";

    
    if (!form.country_id) e.country_id = "Country is required.";
    if (!form.state_id) e.state_id = "State is required.";
    if (!form.city.trim()) e.city = "City is required.";
    if (!form.pincode.trim()) e.pincode = "Pincode is required.";

    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrors(e);

    setSaving(true);
    setApiError("");

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const payload = {
        site_id: editData?.site_id ?? 0,
        site_name: form.site_name,
        site_type: form.site_type,
      
        legal_entity_id: Number(form.legal_entity_id) || 0,
        address: form.address,

      
        country_id: Number(form.country_id),
        state_id: Number(form.state_id),
        city: form.city,
        pincode: form.pincode,

        org_id: user?.org_id ?? 1,
        is_active: true,
      };

      const response = await apiClient.post("/api/sites-locations", payload);

      if (response.data.code === 200) {
        onSaved(
          response.data?.data ?? { ...(editData ?? {}), ...payload },
          !!editData
        );
      } 
      else {
        throw new Error(response.data.message || "Save failed");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong.";
      setApiError(msg);
    } finally {
      setSaving(false);
    }
  };

  //Country dropdown API

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await apiClient.get(
          "/api/country-industries"
        );
        setCountries(res.data.countries || []);
      } catch (err) {
        console.error("Country API error", err);
      }
    };

    loadCountries();
  }, []);

  //Country change handler
  const handleCountryChange = async (e, isInit = false) => {
    const id = e.target.value;
    const selected = countries.find((c) => c.id == id);

    setForm((prev) => ({
      ...prev,
      country: selected?.country_name || "",
      country_id: id,
      state: isInit ? prev.state : "",
      state_id: isInit ? prev.state_id : "",
    }));

    try {
      const res = await apiClient.get(`/api/states?country_id=${id}`);
      setStates(res.data.data || []);
    } catch (err) {
      console.error("State API error", err);
    }
  };

  //State API
  useEffect(() => {
  const loadStatesForEdit = async () => {
    if (!form.country_id) return;

    try {
      const res = await apiClient.get(
        `/api/states?country_id=${form.country_id}`
      );

      setStates(res.data.data || []);
    } catch (err) {
      console.error("State prefill error", err);
    }
  };

  loadStatesForEdit();
}, [form.country_id]);

  // State change handler

  const handleStateChange = (e) => {
    const id = e.target.value;
    const selected = states.find((s) => s.id == id);

    setForm((prev) => ({
      ...prev,
      state_id: id,
      state: selected?.state_name || "",
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {editData ? "Edit Location" : "Add Location"}
          </div>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">Loading options…</div>
          ) : (
            <>
               {/* Legal Entity */}
              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Linked Legal Entity <span className="req">*</span>
                  </label>
                  <select
                    className={`org-field-input ${
                      errors.legal_entity_id ? "input-error" : ""
                    }`}
                    value={String(form.legal_entity_id ?? "")}
                    onChange={handleChange("legal_entity_id")}
                    disabled={saving || entitiesLoading}
                  >
                    <option value="">
                      {entitiesLoading
                        ? "Loading..."
                        : "— Select Legal Entity —"}
                    </option>
                    {legalEntities.map((entity) => (
                      <option
                        key={entity.entity_id}
                        value={String(entity.entity_id)}
                      >
                        {entity.entity_name}
                      </option>
                    ))}
                  </select>
                  {errors.legal_entity_id && (
                    <span className="field-error">
                      ⚠ {errors.legal_entity_id}
                    </span>
                  )}
                </div>
              </div>

                  {/* Site Type */}
              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Location Type <span className="req">*</span>
                  </label>
                  <select
                    className={`org-field-input ${
                      errors.site_type ? "input-error" : ""
                    }`}
                    value={form.site_type}
                    onChange={handleChange("site_type")}
                    disabled={saving}
                  >
                    <option value="">— Select Location Type —</option>
                    {siteTypes.map((t) => (
                      <option key={t.sitetype_id} value={t.sitetype_name}>
                        {t.sitetype_name}
                      </option>
                    ))}
                  </select>
                  {errors.site_type && (
                    <span className="field-error">⚠ {errors.site_type}</span>
                  )}
                </div>
              </div>

              {/* Site Name */}
              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Location Name <span className="req">*</span>
                  </label>
                  <input
                    className={`org-field-input ${
                      errors.site_name ? "input-error" : ""
                    }`}
                    placeholder="e.g. Jamshedpur Plant"
                    value={form.site_name}
                    onChange={handleChange("site_name")}
                    disabled={saving}
                  />
                  {errors.site_name && (
                    <span className="field-error">⚠ {errors.site_name}</span>
                  )}
                </div>
              </div>

             

            

              {/* Country and State */}
              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Country <span className="req">*</span>
                  </label>
                  <select
                    className={`org-field-input ${
                      errors.country_id ? "input-error" : ""
                    }`}
                    value={form.country_id}
                    onChange={handleCountryChange}
                    disabled={saving}
                  >
                    <option value="">— Select Country —</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.country_name}
                      </option>
                    ))}
                  </select>
                  {errors.country_id && (
                    <span className="field-error">⚠ {errors.country_id}</span>
                  )}
                </div>
              </div>

              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    State <span className="req">*</span>
                  </label>
                  <select
                    className={`org-field-input ${
                      errors.state_id ? "input-error" : ""
                    }`}
                    value={form.state_id}
                    onChange={handleStateChange}
                    disabled={!form.country_id || saving}
                  >
                    <option value="">— Select State —</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.state_name}
                      </option>
                    ))}
                  </select>
                  {errors.state_id && (
                    <span className="field-error">⚠ {errors.state_id}</span>
                  )}
                </div>
              </div>

              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    City <span className="req">*</span>
                  </label>
                  <input
                    className={`org-field-input ${
                      errors.city ? "input-error" : ""
                    }`}
                    placeholder="Enter city"
                    value={form.city}
                    onChange={handleChange("city")}
                    disabled={saving}
                  />
                  {errors.city && (
                    <span className="field-error">⚠ {errors.city}</span>
                  )}
                </div>
              </div>

              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Pincode <span className="req">*</span>
                  </label>
                  <input
                    className={`org-field-input ${
                      errors.pincode ? "input-error" : ""
                    }`}
                    placeholder="Enter pincode"
                    value={form.pincode}
                    onChange={handleChange("pincode")}
                    disabled={saving}
                  />
                  {errors.pincode && (
                    <span className="field-error">⚠ {errors.pincode}</span>
                  )}
                </div>
              </div>

                {/* Address */}
              <div
                className="org-form-row"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <div className="org-form-field">
                  <label className="org-field-label">
                    Address <span className="req">*</span>
                  </label>
                  <textarea
                    className={`org-field-input ${
                      errors.address ? "input-error" : ""
                    }`}
                    placeholder="e.g. 456 Industrial Area, Pune 411001"
                    value={form.address}
                    onChange={handleChange("address")}
                    disabled={saving}
                    rows={3}
                  />
                  {errors.address && (
                    <span className="field-error">⚠ {errors.address}</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {apiError && <span className="org-save-error">✕ {apiError}</span>}
          <button
            className="org-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="org-btn-save"
            onClick={handleSubmit}
            disabled={loading || saving}
          >
            {saving ? "Saving…" : editData ? "Update" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteModal;
